import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSystemRoles } from '@/hooks/useSystemRoles';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Clock, 
  AlertTriangle, 
  Wallet,
  Calendar,
  Store,
  Timer,
  TrendingUp,
  TrendingDown,
  Settings,
  Lock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LateOpeningConfig {
  id: string;
  company_id: string;
  branch_id?: string;
  station_id?: string;
  expected_opening_time: string;  // "08:00:00"
  grace_period_minutes: number;   // 15 minutes
  fine_amount_per_minute: number; // Q5.00 per minute
  max_daily_fine: number;         // Q100.00 max per day
  is_active: boolean;
}

interface LateOpeningRecord {
  id: string;
  session_id: string;
  user_id: string;
  branch_name: string;
  station_name: string;
  expected_time: string;
  actual_opening_time: string;
  minutes_late: number;
  fine_amount: number;
  opening_date: string;
  status: 'pending' | 'paid' | 'waived';
}

interface LateOpeningStats {
  today: {
    is_late: boolean;
    minutes_late: number;
    fine_amount: number;
    status: 'on_time' | 'late' | 'critical';
  };
  this_month: {
    late_count: number;
    total_fines: number;
    average_delay: number;
  };
  trend: 'improving' | 'worsening' | 'stable';
}

export function LateOpeningTracker() {
  const { user, selectedCompany } = useAuth();
  const { isSuperAdmin, isCompanyAdmin, canManageSystem } = useSystemRoles();
  const navigate = useNavigate();
  const [stats, setStats] = useState<LateOpeningStats>({
    today: {
      is_late: false,
      minutes_late: 0,
      fine_amount: 0,
      status: 'on_time'
    },
    this_month: {
      late_count: 0,
      total_fines: 0,
      average_delay: 0
    },
    trend: 'stable'
  });
  const [recentRecords, setRecentRecords] = useState<LateOpeningRecord[]>([]);
  const [config, setConfig] = useState<LateOpeningConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const loadLateOpeningData = useCallback(async () => {
    if (!selectedCompany?.id || !user?.id) return;

    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      // Cargar configuración REAL de apertura desde la base de datos
      const { data: configData, error: configError } = await supabase
        .from('cash_opening_config')
        .select('*')
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (configError && configError.code !== 'PGRST116') {
        console.error('Error loading config:', configError);
      }

      if (configData) {
        const realConfig: LateOpeningConfig = {
          id: configData.id,
          company_id: configData.company_id,
          branch_id: configData.branch_id || undefined,
          station_id: configData.cash_station_id || undefined,
          expected_opening_time: configData.expected_opening_time,
          grace_period_minutes: configData.grace_period_minutes,
          fine_amount_per_minute: configData.fine_per_minute,
          max_daily_fine: configData.max_daily_fine,
          is_active: configData.is_active
        };
        setConfig(realConfig);
      } else {
        // Si no hay configuración, usar valores por defecto
        const defaultConfig: LateOpeningConfig = {
          id: 'default',
          company_id: selectedCompany.id,
          expected_opening_time: '09:00:00',
          grace_period_minutes: 5,
          fine_amount_per_minute: 5.00,
          max_daily_fine: 100.00,
          is_active: false
        };
        setConfig(defaultConfig);
      }

      // Obtener multas de aperturas tardías del mes (datos REALES desde applied_fines)
      const { data: fines, error: finesError } = await supabase
        .from('applied_fines')
        .select('*')
        .eq('company_id', selectedCompany.id)
        .like('fine_code', 'AT-%')
        .gte('fine_date', startOfMonth.toISOString().split('T')[0])
        .order('fine_date', { ascending: false });

      if (finesError) {
        console.error('Error loading fines:', finesError);
      }

      // Procesar datos de aperturas tardías desde las multas REALES
      const lateRecords: LateOpeningRecord[] = [];
      let todayRecord: LateOpeningRecord | null = null;

      if (config) {
        fines?.forEach(fine => {
          // Extraer minutos de retraso de la razón (ej: "Apertura tardía: 25 minutos de retraso")
          const minutesMatch = fine.fine_reason?.match(/(\d+)\s+minutos/);
          const minutesLate = minutesMatch ? parseInt(minutesMatch[1]) : 0;

          const record: LateOpeningRecord = {
            id: fine.id,
            session_id: fine.id, // Usar el ID de la multa como referencia
            user_id: fine.employee_id,
            branch_name: 'Sucursal', // Podría mejorarse con JOIN
            station_name: 'Estación', // Podría mejorarse con JOIN
            expected_time: config.expected_opening_time,
            actual_opening_time: '', // No disponible directamente en applied_fines
            minutes_late: minutesLate,
            fine_amount: fine.fine_amount,
            opening_date: fine.fine_date,
            status: fine.status as 'pending' | 'paid' | 'waived'
          };

          lateRecords.push(record);

          if (fine.fine_date === today) {
            todayRecord = record;
          }
        });
      }

      // Calcular estadísticas
      const thisMonthRecords = lateRecords.filter(r => r.opening_date >= startOfMonth.toISOString().split('T')[0]);
      const totalFines = thisMonthRecords.reduce((sum, r) => sum + r.fine_amount, 0);
      const averageDelay = thisMonthRecords.length > 0 
        ? thisMonthRecords.reduce((sum, r) => sum + r.minutes_late, 0) / thisMonthRecords.length
        : 0;

      // Determinar estado de hoy
      let todayStatus: 'on_time' | 'late' | 'critical' = 'on_time';
      if (todayRecord) {
        todayStatus = todayRecord.minutes_late > 30 ? 'critical' : 'late';
      }

      // Calcular tendencia (comparar con mes anterior)
      const lastMonthStart = new Date(startOfMonth);
      lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
      const lastMonthRecords = lateRecords.filter(r => {
        const recordDate = new Date(r.opening_date);
        return recordDate >= lastMonthStart && recordDate < startOfMonth;
      });

      let trend: 'improving' | 'worsening' | 'stable' = 'stable';
      if (thisMonthRecords.length < lastMonthRecords.length) {
        trend = 'improving';
      } else if (thisMonthRecords.length > lastMonthRecords.length) {
        trend = 'worsening';
      }

      setStats({
        today: {
          is_late: !!todayRecord,
          minutes_late: todayRecord?.minutes_late || 0,
          fine_amount: todayRecord?.fine_amount || 0,
          status: todayStatus
        },
        this_month: {
          late_count: thisMonthRecords.length,
          total_fines: totalFines,
          average_delay: averageDelay
        },
        trend
      });

      setRecentRecords(lateRecords.slice(0, 5));

    } catch (error) {
      console.error('Error loading late opening data:', error);
      // Datos demo en caso de error
      setStats({
        today: {
          is_late: true,
          minutes_late: 25,
          fine_amount: 75.00,
          status: 'late'
        },
        this_month: {
          late_count: 8,
          total_fines: 340.00,
          average_delay: 18.5
        },
        trend: 'worsening'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.id, user?.id]);

  useEffect(() => {
    loadLateOpeningData();
    
    // Actualizar cada 5 minutos
    const interval = setInterval(loadLateOpeningData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [loadLateOpeningData]);

  const { formatCurrency } = useTenantConfig();

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'critical':
        return {
          color: 'text-status-error',
          bgColor: 'bg-status-error-bg',
          icon: AlertTriangle,
          text: 'CRÍTICO',
          description: 'Retraso excesivo'
        };
      case 'late':
        return {
          color: 'text-warning',
          bgColor: 'bg-status-warning-bg',
          icon: Clock,
          text: 'TARDÍO',
          description: 'Fuera de horario'
        };
      default:
        return {
          color: 'text-status-success',
          bgColor: 'bg-status-success-bg',
          icon: Timer,
          text: 'PUNTUAL',
          description: 'Apertura a tiempo'
        };
    }
  };

  const getTrendIcon = () => {
    switch (stats.trend) {
      case 'improving':
        return <TrendingDown className="h-4 w-4 text-status-success" />;
      case 'worsening':
        return <TrendingUp className="h-4 w-4 text-status-error" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const statusConfig = getStatusConfig(stats.today.status);
  const StatusIcon = statusConfig.icon;

  if (loading) {
    return (
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <div className="animate-pulse">
            <div className="h-6 bg-muted/50 rounded w-48 mb-2"></div>
            <div className="h-4 bg-muted/50 rounded w-32"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-muted/50 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-lg font-semibold">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
            <Clock className="h-4 w-4 text-primary" />
          </div>
          Control de Aperturas
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground font-medium">
          Puntualidad y multas automáticas
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Today's Status */}
        {stats.today.is_late && (
          <Alert className={`border-l-4 ${statusConfig.color.replace('text-', 'border-l-')} ${statusConfig.bgColor}`}>
            <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
            <AlertDescription className="font-medium">
              <div className="flex items-center justify-between">
                <span>{statusConfig.text}: {formatTime(stats.today.minutes_late)} de retraso</span>
                <Badge variant="destructive" className="text-xs">
                  Multa: {formatCurrency(stats.today.fine_amount)}
                </Badge>
              </div>
              <p className="text-xs mt-1 opacity-80">{statusConfig.description}</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Configuration Info */}
        {config && (
          <div className="bg-background/60 backdrop-blur-sm rounded-xl p-4 border border-border/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Settings className="h-4 w-4 text-status-info" />
                <h4 className="font-semibold text-foreground">Configuración Actual</h4>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Hora esperada:</span>
                <p className="font-semibold">{config.expected_opening_time.slice(0, 5)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Periodo de gracia:</span>
                <p className="font-semibold">{config.grace_period_minutes} minutos</p>
              </div>
              <div>
                <span className="text-muted-foreground">Multa por minuto:</span>
                <p className="font-semibold">{formatCurrency(config.fine_amount_per_minute)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Multa máxima diaria:</span>
                <p className="font-semibold">{formatCurrency(config.max_daily_fine)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Monthly Stats */}
        <div className="bg-background/60 backdrop-blur-sm rounded-xl p-4 border border-border/20">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-semibold text-foreground">Estadísticas del Mes</h4>
            </div>
            {getTrendIcon()}
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-status-error">
                {stats.this_month.late_count}
              </p>
              <p className="text-xs text-muted-foreground">Días tarde</p>
            </div>
            <div>
              <p className="text-lg font-bold text-warning">
                {formatCurrency(stats.this_month.total_fines)}
              </p>
              <p className="text-xs text-muted-foreground">Total multas</p>
            </div>
            <div>
              <p className="text-lg font-bold text-muted-foreground">
                {formatTime(Math.round(stats.this_month.average_delay))}
              </p>
              <p className="text-xs text-muted-foreground">Retraso promedio</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          {(isSuperAdmin || isCompanyAdmin) ? (
            <Button 
              variant="outline" 
              onClick={() => navigate('/cash-control/opening-config')}
              className="flex-1 text-xs border-border hover:bg-secondary/50 h-9"
            >
              <Settings className="h-3 w-3 mr-1" />
              <span className="truncate">Configurar</span>
            </Button>
          ) : (
            <Button 
              variant="outline" 
              disabled
              className="flex-1 text-xs border-border h-9 cursor-not-allowed opacity-50"
              title="Solo administradores pueden configurar"
            >
              <Lock className="h-3 w-3 mr-1" />
              <span className="truncate">Configurar</span>
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => navigate('/cash-control/fines')}
            className="flex-1 text-xs border-border hover:bg-secondary/50 h-9"
          >
            <Wallet className="h-3 w-3 mr-1" />
            <span className="truncate">Ver Multas</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}